import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, FileSpreadsheet, Sparkles, FileText, Search, Columns3, Mail, ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import PasswordStrength from "@/components/PasswordStrength";
import { StripeBadge } from "@/components/StripeBadge";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const COPY = {
  both: {
    label: "Contractors & Vendors",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    headline: <>One platform.<br /><span className="text-primary">Both sides of the quote.</span></>,
    intro: "Contractors request and compare quotations. Suppliers and subcontractors price BOQs automatically. Sign in and we take you to the right place.",
    registerTitle: "Create your account",
    registerSub: "Pick whether you buy or supply and you are set up in a minute.",
    features: [
      { icon: Search, title: "For contractors", desc: "Find vendors, send RFQs, compare quotes side by side." },
      { icon: Sparkles, title: "For vendors", desc: "Auto-price incoming BOQs from your own rate list." },
      { icon: Columns3, title: "One inbox", desc: "Every quotation and RFQ in one place, not ten email threads." },
    ],
  },
  vendor: {
    label: "Supplier / Subcontractor",
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    headline: <>Faster Quotes.<br /><span className="text-primary">More Opportunities.</span></>,
    intro: "Built for contracting and procurement vendors. Match, price and quote — without the spreadsheet gymnastics.",
    registerTitle: "Create your vendor account",
    registerSub: "Import your rate list and start quoting BOQs automatically.",
    features: [
      { icon: FileSpreadsheet, title: "Excel Catalogue", desc: "Upload your rate list once, search instantly." },
      { icon: Sparkles, title: "AI BOQ Matching", desc: "Auto-match contractor items to your pricing." },
      { icon: FileText, title: "Instant Quotations", desc: "Generate polished PDF quotes in seconds." },
    ],
  },
  buyer: {
    label: "Contractor",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    headline: <>Receive and Compare<br /><span className="text-primary">Quotations Instantly.</span></>,
    intro: "Send one RFQ to every vendor you trust, then read all their prices in a single grid instead of ten inboxes.",
    registerTitle: "Create your contractor account",
    registerSub: "Free for your first 30 RFQs and comparisons every month.",
    features: [
      { icon: Search, title: "Vetted Vendor Search", desc: "Find suppliers by trade with live licence status." },
      { icon: Mail, title: "RFQs Your Way", desc: "Send via Matiqs or straight to a vendor's inbox." },
      { icon: Columns3, title: "Quote Comparison", desc: "Totals and line-by-line detail, cheapest highlighted." },
    ],
  },
  einvoice: {
    label: "Matiqs Qonnect · e-invoicing",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    headline: <>Compliant tax invoices<br /><span className="text-primary">in minutes.</span></>,
    intro: "UAE FTA e-invoicing for every business — freelancers, shops, agencies and SMEs. Add your line items and export a compliant invoice. No ERP, no construction setup.",
    registerTitle: "Create your Matiqs Qonnect account",
    registerSub: "Just your name, email and password — you can add your TRN, logo and Peppol ID later.",
    features: [
      { icon: FileText, title: "Standard tax invoices", desc: "Line items, 5% VAT, zero-rated & exempt — for any business." },
      { icon: FileSpreadsheet, title: "Compliant exports", desc: "Branded PDF, Excel, CSV and Peppol UBL XML." },
      { icon: Sparkles, title: "No ERP needed", desc: "Raise your first invoice in minutes, pay only for what you bill." },
    ],
  },
};

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const invite = (params.get("ref") || "").toUpperCase();
  const initialType = ["buyer", "einvoice"].includes(params.get("type")) ? params.get("type") : "vendor";
  const [accountType, setAccountType] = useState(initialType);
  const [mode, setMode] = useState(invite || params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [referral, setReferral] = useState(invite);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/public/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (params.get("reason") === "session") {
      setError("You were signed out because your account reached its two-device limit and signed in "
               + "somewhere else. Sign in again to continue.");
    }
  }, [params]);

  const c = COPY[mode === "login" && !params.get("type") ? "both" : accountType];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const acctType = accountType === "buyer" ? "contractor" : accountType === "einvoice" ? "einvoice" : "vendor";
      const u =
        mode === "login"
          ? await login(email, password)
          : await register(email, password, company, referral.trim(),
                           acctType, accountType === "einvoice" ? "United Arab Emirates" : country);
      toast.success("Welcome to Matiqs");
      if (u?.account_type === "contractor") {
        navigate("/marketplace");
      } else if (u?.account_type === "einvoice") {
        navigate("/einvoicing/app");
      } else {
        // Vendors land on their dashboard once they have any RFQ activity
        // (quotations or incoming RFQs); brand-new vendors see the For Vendors onboarding page.
        try {
          const { data } = await api.get("/stats");
          navigate((data.rfq_count > 0 || data.opportunity_count > 0) ? "/dashboard" : "/for-vendors");
        } catch {
          navigate("/dashboard");
        }
      }
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left banner */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-slate-900 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 transition-opacity duration-700"
          style={{ backgroundImage: `url(${c.image})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/70 to-slate-900/40" />
        <Link to="/" className="relative z-10 flex items-center gap-3 group">
          <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-9 w-auto" />
          <div className="text-xs uppercase tracking-[0.18em] text-slate-400 border-l border-white/15 pl-3 group-hover:text-slate-200 transition-colors">
            {c.label}
          </div>
        </Link>

        <div className="relative z-10 space-y-8">
          <div>
            <h1 data-testid="login-banner-headline" className="font-display text-4xl font-extrabold text-white leading-tight">
              {c.headline}
            </h1>
            <p className="text-slate-300 mt-4 max-w-md leading-relaxed">{c.intro}</p>
          </div>
          <div className="space-y-4">
            {c.features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.12 * i + 0.05 }}
                className="flex items-start gap-3"
              >
                <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
                  <f.icon className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{f.title}</div>
                  <div className="text-slate-400 text-sm">{f.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">© {new Date().getFullYear()} Matiqs. All rights reserved.</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="lg:hidden">
              <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-8 w-auto" />
            </Link>
            <Link data-testid="back-home-link" to="/" className="hidden lg:inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
          </div>

          <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100">
            {mode === "login" ? "Welcome back" : c.registerTitle}
          </h2>
          <p className="text-muted-foreground text-sm mt-1 mb-6">
            {mode === "login"
              ? "One sign-in for contractors and vendors — we will take you to the right place."
              : c.registerSub}
          </p>

          <div className="flex p-1 bg-secondary rounded-lg mb-5">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${mode === m ? "bg-card shadow-sm text-primary" : "text-muted-foreground"}`}
              >
                {m === "login" ? "Sign In" : "Sign Up"}
              </button>
            ))}
          </div>

          {mode === "register" && (
            <div className="mb-5">
              <Label className="mb-2 block">I am signing up as</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { id: "buyer", label: "Contractor", hint: "I need quotations" },
                  { id: "vendor", label: "Supplier / Vendor", hint: "I send quotations" },
                  { id: "einvoice", label: "Any business — e-invoicing", hint: "I need tax invoices" },
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`account-type-${t.id}`}
                    onClick={() => setAccountType(t.id)}
                    className={`text-left px-3.5 py-3 rounded-xl border transition-all ${accountType === t.id ? "border-primary bg-primary/5 shadow-sm" : "border-border bg-card hover:border-primary/40"}`}
                  >
                    <div className={`text-sm font-bold ${accountType === t.id ? "text-primary" : "text-slate-900 dark:text-slate-100"}`}>{t.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="company">{accountType === "einvoice" ? "Business / your name" : "Company Name"}</Label>
                <Input
                  id="company"
                  data-testid="company-input"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder={accountType === "einvoice" ? "e.g. Jane Doe or Acme Services FZ-LLC" : "e.g. Acme Contracting LLC"}
                  required
                  className="mt-1.5"
                />
              </div>
            )}
            {mode === "register" && accountType !== "einvoice" && (
              <div>
                <Label htmlFor="country">Country</Label>
                <select
                  id="country"
                  data-testid="country-select"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                  className="mt-1.5 flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="" disabled>Select your country</option>
                  {countries.map((cn) => <option key={cn} value={cn}>{cn}</option>)}
                </select>
                <p className="text-xs text-muted-foreground mt-1.5">We'll show you vendors, RFQs and comparisons in your country by default.</p>
              </div>
            )}
            <div>
              <Label htmlFor="email">{accountType === "einvoice" && mode === "register" ? "Email" : "Company Email"}</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === "login" && (
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-primary hover:underline">
                    Forgot password?
                  </Link>
                )}
              </div>
              <PasswordInput
                id="password"
                data-testid="password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5"
              />
              {mode === "register" && <PasswordStrength password={password} />}
            </div>

            {mode === "register" && (
              <div>
                <Label htmlFor="referral">Referral code <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  id="referral"
                  data-testid="referral-code-input"
                  value={referral}
                  onChange={(e) => setReferral(e.target.value.toUpperCase())}
                  placeholder="e.g. ACME-4F2A"
                  className="mt-1.5 font-mono"
                />
                {invite && (
                  <p data-testid="referral-applied-note" className="text-xs text-brand font-semibold mt-1.5">
                    Invite applied — you get 20% off your first paid month.
                  </p>
                )}
              </div>
            )}

            {error && (
              <div data-testid="auth-error" className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="w-full h-11 text-sm font-semibold active:scale-[0.98] transition-transform"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign In" : "Create Account"}
            </Button>

            {mode === "register" && accountType === "buyer" && (
              <div className="flex flex-col items-center gap-1.5 pt-1" data-testid="signup-trust-strip">
                <StripeBadge testid="signup-stripe-badge" />
                <p className="text-xs text-muted-foreground">30-day free trial · cancel anytime · no hidden fees</p>
              </div>
            )}
          </form>
        </motion.div>
      </div>
    </div>
  );
}
